import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetAllDocumentsRequestList,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
  useRiskSummariesByIds,
} from "../custom-hooks/queries";
import useRequestListStickyFilters from "../custom-hooks/useRequestListStickyFilters";
import { extractRiskRowIds } from "../utils/riskSummaryGridUtils";
import SkeletonLoader from "../components/SkeletonLoader";
import Pagination from "../components/CustomPagination";
import Notification from "../components/Notification";
import { useDispatch, useSelector } from "react-redux";
import { setSearchTextByPage } from "../redux-store/features/providerFiltersSlice";
import PageToolbarFilters from "../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../components/Filters/ToolbarSearch";
import { generateV2ServiceCategoryOptions } from "../utils/serviceCategoryV2Mapping";
import TableExportMenu from "../components/Menus/TableExportMenu";
import { documentsCellStyles } from "../utils/exportBadgeStyles";
import {
  getLibraryRequestsExportHeaders,
  getLibraryRequestsTableRows,
  LIBRARY_REQUESTS_DEFAULT_SORT_CONFIG,
  LIBRARY_REQUESTS_USE_SERVER_SORT,
  mapLibraryRequestItemToExportRecord,
  sortConfigToLibraryRequestsApiParams,
} from "../utils/libraryRequestsGridMapper";
import LibraryRequestsGridTable from "../components/LibraryRequests/LibraryRequestsGridTable";
import { REQUEST_LIST_SCREEN_KEYS } from "../utils/requestListStickyFilterPersistence";

const SPONSOR_LIBRARY_EMPTY_SELECTED_IDS = {
  docType: [],
  provider: [],
  status: [],
  services: [],
};

export const SponsorVQAGrid = () => {
  const [show, setShow] = useState(false);

  const { data: providers } = useGetprovidersListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const dispatch = useDispatch();

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["myDocuments"]
    ) || "";

  const {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
  } = useRequestListStickyFilters({
    screenKey: REQUEST_LIST_SCREEN_KEYS.SPONSOR_LIBRARY_REQUESTS,
    emptySelectedIds: SPONSOR_LIBRARY_EMPTY_SELECTED_IDS,
    defaultSortConfig: LIBRARY_REQUESTS_DEFAULT_SORT_CONFIG,
    searchQuery,
    useServerSort: LIBRARY_REQUESTS_USE_SERVER_SORT,
  });

  const filters = useMemo(
    () => [
      {
        id: "provider",
        name: "Vendor",
        searchable: true,
        options: providers || [],
        total: 1335,
      },
      {
        id: "docType",
        name: "Document Type",
        searchable: false,
        options: [
          { docId: "RFI", label: "VQQ", checked: false },
          { docId: "VQA", label: "Audit Report", checked: false },
        ],
        total: 2,
      },
      {
        id: "services",
        name: "Scope",
        searchable: true,
        isV2: true,
        options: v2ServiceOptions,
        total: v2ServiceOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Released", label: "Released", checked: false },
          { statusID: "Pending", label: "Pending", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
        ],
        total: 3,
      },
    ],
    [providers, v2ServiceOptions]
  );

  const serviceNames = useMemo(() => {
    return (
      selectedIds.services?.map((serviceId) => {
        const service = rfis?.find(
          (rfi) =>
            rfi.rfi_id === serviceId ||
            rfi.id === serviceId ||
            rfi.hash_id === serviceId
        );
        return (
          service?.services ||
          service?.rfi_name ||
          service?.name ||
          serviceId
        );
      }) || []
    );
  }, [selectedIds.services, rfis]);
  const rfiIds = [];

  const serverSortParams = useMemo(() => {
    if (!LIBRARY_REQUESTS_USE_SERVER_SORT) return undefined;
    const { orderBy, sortDirection } =
      sortConfigToLibraryRequestsApiParams(sortConfig);
    if (!orderBy || !sortDirection) return undefined;
    return { orderBy, sortDirection };
  }, [sortConfig]);

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllDocumentsRequestList(
    currentPage,
    selectedIds.docType,
    selectedIds.provider,
    selectedIds.status,
    rfiIds,
    [],
    searchQuery,
    undefined,
    serviceNames,
    serverSortParams,
    sortFetchNonce
  );

  const sortedItems = useMemo(
    () =>
      getLibraryRequestsTableRows(
        requestList,
        sortConfig,
        LIBRARY_REQUESTS_USE_SERVER_SORT,
        "sponsor"
      ),
    [requestList, sortConfig]
  );

  const exportDataRef = useRef(null);

  // 14029 — Pre-fetch published risk summaries for the visible page (export only
  // emits a Risk Summary cell where a published snapshot exists).
  const exportRowIds = useMemo(() => extractRiskRowIds(sortedItems), [sortedItems]);
  const { data: exportRiskSummariesRaw } = useRiskSummariesByIds(exportRowIds, {
    enabled: exportRowIds.length > 0,
  });
  const exportRiskSummaries = exportRiskSummariesRaw ?? {};

  useEffect(() => {
    exportDataRef.current = () => {
      const records = sortedItems.map((item) => {
        const id =
          item?.sponsor_rfi_response_id ?? item?.entity_id ?? null;
        const riskSummary = id ? exportRiskSummaries[id] : null;
        return mapLibraryRequestItemToExportRecord(item, {
          audience: "sponsor",
          riskSummary,
        });
      });
      return {
        records,
        headers: getLibraryRequestsExportHeaders("sponsor"),
      };
    };
  }, [sortedItems, exportRiskSummaries]);

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">
              Library Requests
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              The table below displays all bookmarked and requested Vendor Qualification Questionnaires (VQQ) and Audit Reports from auditor-led qualification assessments in the Diligent document library.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="Library Requests"
              reportSubtitle="Documents requested from your vendors"
              filePrefix="Library_Requests"
              downloadType="Library Requests"
              cellStyles={documentsCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: "myDocuments",
                    searchText: text,
                  })
                )
              }
              placeholder="Search documents..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              serviceCategoryV2SourceData={rfis || []}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
            <LibraryRequestsGridTable
              data={sortedItems}
              sortConfig={sortConfig}
              onSort={handleSort}
            refetch={refetch}
            isSearchActive={!!searchQuery}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={
                  requestList?.document_request_for_sponsor?.total_pages ||
                  requestList?.document_request_for_provider?.total_pages ||
                  1
                }
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
